import React, { useCallback, useContext, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import ScheduleReportModal from 'components/ScheduleReportModal';

import AccountPickerContext from 'contexts/AccountPickerContext';
import ContentUsage from 'components/ContentUsage';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ReportContext from 'contexts/ReportContext';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import createMetricListGadget from 'api/metricListGadgets/createMetricListGadget';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import usePopup from 'hooks/usePopup';
import aguid from 'lib/aguid';

import AddScorecardForm from '../Dashboard/AddScorecardForm';
import GenerateMetricReportForm from '../../screens/Reports/GenerateMetricReportForm';
import IFrameForm from '../IFrameForm';
import ImageCardEditForm from '../ImageGadget/ImageCardEditForm';
import ReportForm from '../ReportForm';
import ReportQuickFiltersModal from '../Report/QuickFilters';
import PinnedFiltersModal from '../Report/PinnedFilters';
import TextCardForm from '../TextCardForm';
import VideoCardForm from '../VideoCardForm';
import ReportMenu from './ReportMenu';
import useOnValidationReportRemoved from './useOnValidationReportMoved';

const ReportMenuContainer = ({
  startEditing,
  saveReportAs,
  onDelete,
  onAddVisualization,
  setCurrentCanvas,
  canvasMode,
}: {
  startEditing: () => void;
  saveReportAs: (name: string, labelIds: string[]) => Promise<void>;
  onDelete: () => void;
  onAddVisualization?: () => void;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  canvasMode: CanvasMode;
}) => {
  const { report } = useContext(ReportContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { onValidationReportRemoved, isValidationReport } =
    useOnValidationReportRemoved();

  const {
    isOpen: saveAsIsOpen,
    open: openSaveAs,
    close: closeSaveAs,
  } = usePopup();
  const {
    isOpen: isSettingsOpen,
    open: openSettings,
    close: closeSettings,
  } = usePopup();
  const {
    isOpen: isShowUsageOpen,
    open: openShowUsage,
    close: closeShowUsage,
  } = usePopup();
  const {
    isOpen: isQuickFiltersOpen,
    open: openQuickFilters,
    close: closeQuickFilters,
  } = usePopup();
  const {
    isOpen: isPinnedFiltersOpen,
    open: openPinnedFilters,
    close: closePinnedFilters,
  } = usePopup();
  const {
    isOpen: isEditGeneratorOpen,
    open: openEditGenerator,
    close: closeEditGenerator,
  } = usePopup();
  const {
    isOpen: isScheduleOpen,
    open: openSchedule,
    close: closeSchedule,
  } = usePopup();
  const {
    isOpen: isIFrameFormOpen,
    open: openIFrameForm,
    close: closeIFrameForm,
  } = usePopup(false);
  const {
    isOpen: isImageCardFormOpen,
    open: openImageCardForm,
    close: closeImageCardForm,
  } = usePopup(false);
  const {
    isOpen: isVideoCardFormOpen,
    open: openVideoCardForm,
    close: closeVideoCardForm,
  } = usePopup(false);
  const {
    isOpen: isTextCardFormOpen,
    open: openTextCardForm,
    close: closeTextCardForm,
  } = usePopup(false);
  const {
    isOpen: isAddScorecardFormOpen,
    open: openAddScorecardForm,
    close: closeAddScorecardForm,
  } = usePopup(false);

  const [savingAs, setSavingAs] = useState(false);

  const onStartEditing = () => {
    startEditing();
  };

  const onOpenSettings = () => {
    if (report?.generatorConfig) {
      openEditGenerator();
    } else {
      openSettings();
    }
  };

  const onSaveAs = ({
    name,
    labelIds,
  }: {
    name: string;
    labelIds: string[];
  }) => {
    setSavingAs(true);
    saveReportAs(name, labelIds).then(() => {
      setSavingAs(false);
      closeSaveAs();
    });
  };

  const onMetricListAdded = useCallback(async () => {
    const metricListId = aguid();
    await createMetricListGadget({
      id: metricListId,
      name: 'Metric List',
      currentUserId,
      accountId: selectedAccountId,
    });

    setCurrentCanvas((c) => {
      const newCard: CanvasCard.MetricListCard = {
        layout: buildCardLayout(0, findStartingY(c.cards, canvasMode)),
        content: {
          type: 'Metric List',
          metricListId,
        },
      };
      return {
        ...c,
        cards: [...c.cards, newCard],
      };
    });
    startEditing();
  }, [
    canvasMode,
    currentUserId,
    selectedAccountId,
    setCurrentCanvas,
    startEditing,
  ]);

  return (
    <React.Fragment>
      <ReportMenu
        onStartEditing={onStartEditing}
        onCreateNewGadgetClicked={onAddVisualization}
        onMetricListAdded={onMetricListAdded}
        onAddScorecardClicked={openAddScorecardForm}
        onImageCardAdded={openImageCardForm}
        openVideoCardForm={openVideoCardForm}
        openTextCardForm={openTextCardForm}
        openIFrameForm={openIFrameForm}
        onOpenSettings={onOpenSettings}
        onOpenQuickFilters={openQuickFilters}
        onOpenPinnedFilters={openPinnedFilters}
        onSaveAs={openSaveAs}
        onDelete={onDelete}
        onValidationReportRemoved={onValidationReportRemoved}
        isValidationReport={isValidationReport}
        report={report}
        openShowUsage={openShowUsage}
        onOpenSchedule={openSchedule}
      />
      <ModalTransition>
        {isSettingsOpen && (
          <Modal>
            <ReportForm isOpen={isSettingsOpen} close={closeSettings} />
          </Modal>
        )}
      </ModalTransition>
      <ModalTransition>
        {saveAsIsOpen && (
          <Modal>
            <ReportForm
              close={closeSaveAs}
              isOpen={saveAsIsOpen}
              onSaveAs={onSaveAs}
              isSaveAs
              isSavingAs={savingAs}
            />
          </Modal>
        )}
      </ModalTransition>
      <AddScorecardForm
        close={closeAddScorecardForm}
        isOpen={isAddScorecardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <IFrameForm
        close={closeIFrameForm}
        isOpen={isIFrameFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      {isImageCardFormOpen && (
        <ImageCardEditForm
          close={closeImageCardForm}
          isOpen={isImageCardFormOpen}
          startEditing={startEditing}
          setCurrentCanvas={setCurrentCanvas}
          canvasMode={canvasMode}
        />
      )}
      <VideoCardForm
        close={closeVideoCardForm}
        isOpen={isVideoCardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <TextCardForm
        close={closeTextCardForm}
        isOpen={isTextCardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <ContentUsage
        isOpen={isShowUsageOpen}
        close={closeShowUsage}
        type={'report'}
      />
      <ReportQuickFiltersModal
        isOpen={isQuickFiltersOpen}
        close={closeQuickFilters}
      />
      <PinnedFiltersModal
        isOpen={isPinnedFiltersOpen}
        close={closePinnedFilters}
      />
      {isEditGeneratorOpen && report && (
        <GenerateMetricReportForm
          close={closeEditGenerator}
          existingReport={report}
        />
      )}
      <ModalTransition>
        {isScheduleOpen && (
          <Modal onClose={closeSchedule}>
            <ScheduleReportModal close={closeSchedule} />
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

export default ReportMenuContainer;
